import React, { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";

const CrazyHeadline = () => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const fRef = useRef(null);
  const ullRef = useRef(null);
  const stackRef = useRef(null);
  const devRef = useRef(null);

  // Get translated text
  const line1 = t("hero.line1");
  const line2 = t("hero.line2");
  const line3 = t("hero.line3");
  const firstChar = line1.charAt(0);
  const restOfLine1 = line1.slice(1);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.5 });

      // Initial setup
      gsap.set(fRef.current, { x: -500, opacity: 0, rotation: -360 });
      gsap.set(ullRef.current, { x: -500, opacity: 0 });
      gsap.set(stackRef.current, { x: "105%" }); // From right (masked)
      gsap.set(devRef.current, { y: "105%" }); // From bottom (masked)

      // Animation
      // 1. F flies in spinning
      tl.to(fRef.current, {
        x: 0,
        opacity: 1,
        rotation: 0,
        duration: 1.5,
        ease: "elastic.out(1, 0.5)",
      });

      // 2. ULL follows quickly
      tl.to(
        ullRef.current,
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power4.out",
        },
        "-=1.2"
      );

      // 3. STACK slides in from right (masked)
      tl.to(
        stackRef.current,
        {
          x: 0,
          duration: 0.8,
          ease: "power3.out",
        },
        "-=0.8"
      );

      // 4. DEVELOPER slides up from bottom (masked)
      tl.to(
        devRef.current,
        {
          y: 0,
          duration: 0.8,
          ease: "power3.out",
        },
        "-=0.6"
      );

      // Crazy hover effect setup
      // We can add event listeners here or just rely on CSS for hover,
      // but user asked for "crazy animation" on load mostly.
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // Styles to match the previous H1 look but allow for individual animations
  const lineStyle = {
    display: "block",
    width: "100%",
    position: "relative",
    padding: "0 10px",
    marginLeft: "-10px",
    overflow: "hidden", // To avoid scrollbars during animation if needed, but might clip 'F' rotation
  };

  // We remove overflow hidden from lines to allow the F to spin freely without clipping
  // But we might need it on the container if things fly too far.
  // Let's trust GSAP to handle it and maybe set overflow-x hidden on body/section globally.

  return (
    <h1 ref={containerRef} style={{ overflow: "visible" }}>
      <span style={{ ...lineStyle, display: "flex" }}>
        <span
          ref={fRef}
          style={{
            display: "inline-block",
            transformOrigin: "center",
            width: "auto",
            margin: 0,
            padding: 0,
          }}
        >
          {firstChar}
        </span>
        <span
          ref={ullRef}
          style={{
            display: "inline-block",
            width: "auto",
            margin: 0,
            padding: 0,
          }}
        >
          {restOfLine1}
        </span>
      </span>

      <span style={lineStyle}>
        <span ref={stackRef} style={{ display: "inline-block" }}>
          {line2}
        </span>
      </span>

      <span style={lineStyle}>
        <span ref={devRef} style={{ display: "inline-block" }}>
          {line3}
        </span>
      </span>
    </h1>
  );
};

export default CrazyHeadline;
